import { type Language, type FieldLimits, characterCount, wordCount } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { minimumText, counterText } from '../language/words.js'

/**
 * De meelopende teller (R-12, R-13).
 *
 * Hij loopt bij elke aanslag mee en laat zien hoeveel er is gebruikt en hoeveel er
 * nog over is. Bij overschrijding kleurt hij; of dat ook indienen blokkeert hangt
 * aan het mechanisme van de regel (hard blokkeert, R-13; een waarschuwing is
 * advies, R-23). De harde melding zelf komt pas bij het verlaten van het veld
 * (R-25) en staat in de veldschil, niet hier.
 *
 * Het tellen gebruikt `aantalTekens` en `aantalWoorden` van de engine, zodat de
 * client en de server dezelfde uitkomst hebben (ontwerp 5.9): de teller die de
 * invuller ziet en de grens die indienen tegenhoudt zijn dan hetzelfde getal.
 *
 * De teller is een `output` met `aria-live="polite"`: hij hoort bij het veld en
 * verandert tijdens het typen. Zonder live region weet een schermlezergebruiker
 * niet dat hij over de grens gaat; assertive zou elke aanslag onderbreken.
 */

export interface CounterProps {
  readonly tekst: string
  readonly limits: FieldLimits | undefined
  readonly taal: Language
  /** Het id waarnaar de veldschil verwijst, zodat de teller bij het veld hoort. */
  readonly id?: string
}

export function Counter({ tekst: text, limits, taal: language, id }: CounterProps): ReactNode {
  if (limits === undefined) return null
  const { maximumTekens, maximumWoorden, minimumWoorden } = limits
  if (maximumTekens === undefined && maximumWoorden === undefined && minimumWoorden === undefined) {
    return null
  }

  const characters = characterCount(text)
  const words = wordCount(text)
  const above =
    (maximumTekens !== undefined && characters > maximumTekens.grens) ||
    (maximumWoorden !== undefined && words > maximumWoorden.grens)
  const innermost = minimumWoorden !== undefined && words < minimumWoorden.grens

  const parts = [
    maximumTekens === undefined ? undefined : counterText('tekens', characters, maximumTekens.grens, language),
    maximumWoorden === undefined ? undefined : counterText('woorden', words, maximumWoorden.grens, language),
    minimumWoorden === undefined ? undefined : minimumText(words, minimumWoorden.grens, language),
  ].filter((part): part is string => part !== undefined)

  return (
    <output
      className="ef-teller"
      id={id}
      aria-live="polite"
      data-stand={above ? 'boven' : innermost ? 'onder' : 'binnen'}
    >
      {parts.join(' · ')}
    </output>
  )
}
